import { Link, Navigate, useParams } from 'react-router-dom';
import { Lock, ArrowSquareOut, CircleNotch } from '@phosphor-icons/react';

import type { ManifestSection, ManifestSetting } from '@/lib/settingsApi';
import { readOnlyWording, sectionMeta, settingLabel, type SettingsActor } from './catalogue';
import { SECTION_COMPONENTS } from './sections';
import { useSettingsBasePath, useSettingsManifest } from './SettingsShell';

/**
 * One Settings section.
 *
 * Phase 3 builds the FRAME, not the editors: each registered setting is listed with its real
 * state — editable, read-only and why, or stored-but-not-yet-acted-on — and the section links to
 * the working page where one already exists, so nothing a person can do today stops working
 * while the individual sections are built out.
 *
 * The one rule that matters here: a read-only setting renders **no write control at all**. Not a
 * disabled input inside a form with a Save button, which invites a person to try and then fail —
 * a plain value, and a line naming who does control it.
 */

/** A setting that is stored but not yet acted on by the surface it names. */
function PartialNote({ setting }: { setting: ManifestSetting }) {
  if (setting.status !== 'partial') return null;
  return (
    <span className="ml-2 rounded border border-amber-500/40 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
      Saved, not yet applied
    </span>
  );
}

function SettingRow({ setting, grantor }: { setting: ManifestSetting; grantor?: string | null }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border/70 px-5 py-3.5 last:border-0">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center text-sm font-medium text-foreground">
          {settingLabel(setting.key)}
          <PartialNote setting={setting} />
        </div>
        {!setting.canWrite && setting.readOnlyReason && (
          <div className="mt-1 flex items-start gap-1.5 text-xs text-muted-foreground">
            <Lock className="mt-0.5 h-3 w-3 shrink-0" />
            <span>{readOnlyWording(setting.readOnlyReason, { grantor })}</span>
          </div>
        )}
      </div>

      {/* No write control is rendered for a read-only setting — see the file header. */}
      <div className="shrink-0 pt-0.5 text-xs font-medium">
        {setting.canWrite ? (
          <span className="text-muted-foreground">Editable</span>
        ) : (
          <span className="rounded-md bg-muted px-2 py-1 text-muted-foreground">View only</span>
        )}
      </div>
    </div>
  );
}

export function SettingsSectionPage() {
  const { sectionId } = useParams<{ sectionId: string }>();
  const { data: manifest, isLoading } = useSettingsManifest();
  const basePath = useSettingsBasePath();

  // `!manifest` is NOT the same as "this section does not exist". On react-query v5 a query that
  // is pending but not currently fetching — a dropped connection on a phone — reports
  // `isLoading: false` with no data, and treating that as a missing section redirected the person
  // out of the section they had just opened. A genuine failure is handled by SettingsShell, which
  // renders its error card instead of this Outlet, so waiting here cannot spin forever.
  if (isLoading || !manifest) {
    return (
      <div className="flex h-48 items-center justify-center" role="status" aria-label="Loading section">
        <CircleNotch size={20} className="animate-spin text-muted-foreground/50" />
      </div>
    );
  }

  const section: ManifestSection | undefined = manifest.sections.find((s) => s.id === sectionId);

  // A section this person is not offered is not a 403 page — it simply is not part of their
  // Settings. Send them to their own Settings rather than telling them what exists elsewhere.
  if (!section) return <Navigate to={basePath} replace />;

  // A section with a real editor renders it. The list below is the fallback for sections still
  // being built — never a substitute for one that has been.
  const Editor = SECTION_COMPONENTS[section.id];
  if (Editor) return <Editor section={section} manifest={manifest!} />;

  const meta = sectionMeta(section.id);
  const readOnlySection = !section.hasWritable;
  // A link is offered ONLY where that actor genuinely has such a page. The application's existing
  // settings pages are role-scoped and asymmetric, so a link that is right for a barrister
  // dead-ends for a clerk. Absent is better than broken.
  const actor = manifest!.context.actor as SettingsActor;
  const pages = (meta.pages ?? [])
    .map((p) => ({ ...p, href: p.to[actor] }))
    .filter((p): p is typeof p & { href: string } => Boolean(p.href));

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-lg font-semibold tracking-tight text-foreground">{meta.label}</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">{meta.description}</p>
      </div>

      {readOnlySection && (
        <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-border bg-muted/40 px-4 py-3">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {readOnlyWording(section.settings[0]?.readOnlyReason ?? 'not-editable')}
          </p>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card">
        {section.settings.map((setting) => (
          <SettingRow key={setting.key} setting={setting} />
        ))}
      </div>

      {pages.length > 0 && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {pages.map((p) => (
            <Link
              key={p.href}
              to={p.href}
              className="group flex items-start justify-between gap-3 rounded-xl border border-border bg-card p-4 transition hover:border-brand-600/40 hover:bg-muted/40"
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold text-foreground">{p.label}</div>
                <div className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{p.description}</div>
              </div>
              <ArrowSquareOut className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground group-hover:text-foreground" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
